import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';

const Avatar = ({ src, name }) =>
  src ? (
    <img src={src} alt={name} className="w-10 h-10 rounded-full object-cover" />
  ) : (
    <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold">
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );

const BlockedUsersScreen = ({ token, API, t, isRTL, onBack }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/me/blocked-users`, { headers });
      setList(res.data || []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [API, token]);

  useEffect(() => {
    load();
  }, [load]);

  const unblock = async (uid) => {
    if (!window.confirm(t('unblockConfirm'))) return;
    setBusy(uid);
    try {
      await axios.delete(`${API}/users/${uid}/block`, { headers });
      setList((arr) => arr.filter((u) => u.id !== uid));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="blocked-users-screen" dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-30 bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={onBack} data-testid="blocked-back" className="flex items-center gap-1 text-purple-600 font-medium">
            <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
            <span className="text-sm">{t('back')}</span>
          </button>
          <h1 className="text-base font-bold text-gray-900">{t('blockedUsers')}</h1>
          <div className="w-12" />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-4">
        {loading ? (
          <div className="py-16 text-center text-gray-500">{t('loading')}</div>
        ) : list.length === 0 ? (
          <div data-testid="blocked-empty" className="py-16 text-center text-gray-500">
            {t('noBlockedUsers')}
          </div>
        ) : (
          <ul className="bg-white rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
            {list.map((u) => (
              <li key={u.id} data-testid={`blocked-row-${u.id}`} className="flex items-center gap-3 px-3 py-3">
                <Avatar src={u.profile_picture_url} name={u.name} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{u.name}</p>
                  {u.nickname && <p className="text-xs text-gray-500 truncate">@{u.nickname}</p>}
                </div>
                <button
                  onClick={() => unblock(u.id)}
                  disabled={busy === u.id}
                  data-testid={`blocked-unblock-${u.id}`}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {t('unblock')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default BlockedUsersScreen;
